import { Injectable } from '@angular/core';
import { isCollection, standardizeUrl } from '@core/utils';
import { getEntityType, getIDType, EntityStore, ID } from '@datorama/akita';
import {
  isID,
  HttpAddConfig,
  HttpDeleteConfig,
  HttpGetConfig,
  HttpUpdateConfig,
  NgEntityService,
} from '@datorama/akita-ng-entity-service';
import { EMPTY, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { DTO } from './dto';
import { Entity } from './entity';
import { envelopeKey, hasPagination, unwrapFromEnvelope } from './entity-keys';
import { Mapper } from './mapper';
import { ResourceBaseUrlBuilder } from './resource-base-url-builder';

type PaginationFilters = {
  [key: string]: string | number | boolean;
};

export type PaginationParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  total?: number;
  filters?: PaginationFilters;
};

export type StatusParams = {
  status?: string;
};

export interface WithPagination<T> {
  entities: T[];
  pagination: PaginationParams;
}

@Injectable({ providedIn: 'root' })
export class ApiEntityService<S> extends NgEntityService<S> {
  private baseUrlSuffix: string = null;
  private overriddenResourceName: string = null;
  private resourcePrefix: string = null;
  private resourceSuffix: string = null;
  private urlSuffix: string = null;

  constructor(
    protected store: EntityStore<S>,
    protected urlBuilder: ResourceBaseUrlBuilder,
    protected mapper: Mapper<Entity, DTO>,
  ) {
    super(store);
  }

  public add<T>(entity: getEntityType<S> | FormData, config?: HttpAddConfig<T>): Observable<T> {
    const baseUrl = this.urlBuilder.buildBaseUrl(this.baseUrlSuffix);

    // Guard against empty baseUrl to prevent API calls when organization data is cleared during logout
    // This returns EMPTY observable which completes immediately without emitting any values
    if (!baseUrl || baseUrl.trim() === '') {
      return EMPTY;
    }

    this.setBaseUrl(baseUrl);
    entity = this.mapRequest<getEntityType<S>>(entity);

    config = {
      url: this.buildUrl(),
      mapResponseFn: (res: unknown) => {
        if (!res) {
          return;
        }
        return this.mapResponse(res, this.mapper);
      },
      ...config,
    };

    this.clearPrefixesAndSuffixes();

    return super.add<T>(entity, config);
  }

  public appendToBaseUrl(value: string): void {
    this.baseUrlSuffix = value;
  }

  public delete<T>(id: getIDType<S>, config?: HttpDeleteConfig<T>): Observable<T> {
    const baseUrl = this.urlBuilder.buildBaseUrl(this.baseUrlSuffix);

    // Guard against empty baseUrl to prevent API calls when organization data is cleared during logout
    // This returns EMPTY observable which completes immediately without emitting any values
    if (!baseUrl || baseUrl.trim() === '') {
      return EMPTY;
    }

    this.setBaseUrl(baseUrl);

    config = {
      url: this.buildUrl(id),
      ...config,
    };

    this.clearPrefixesAndSuffixes();

    return super.delete<T>(id, config);
  }

  public deleteMany<T>(ids: getIDType<S>[]): Observable<T> {
    return this.delete(null, {
      params: {
        // sent as comma separated query param, e.g. DELETE /api/accounts/notifications?uid=2,3,5,8
        uid: ids.map((id) => `${id}`),
      },
    });
  }

  public get<T>(id?: getIDType<S>, config?: HttpGetConfig<T>): Observable<T>;
  public get<T>(config?: HttpGetConfig<T>): Observable<T>;
  public get<T>(idOrConfig?: getIDType<S> | HttpGetConfig<T>, config?: HttpGetConfig<T>): Observable<T> {
    const baseUrl = this.urlBuilder.buildBaseUrl(this.baseUrlSuffix);

    // Guard against empty baseUrl to prevent API calls when organization data is cleared during logout
    // This returns EMPTY observable which completes immediately without emitting any values
    if (!baseUrl || baseUrl.trim() === '') {
      return EMPTY;
    }

    this.setBaseUrl(baseUrl);

    const id = isID(idOrConfig) ? idOrConfig : null;

    config = {
      url: this.buildUrl(id as getIDType<S>),
      mapResponseFn: (res: unknown) => {
        return this.mapResponse(res, this.mapper);
      },
      ...config,
    };

    this.clearPrefixesAndSuffixes();

    if (isID(idOrConfig)) {
      const skipWrite: boolean = config?.skipWrite ?? false;

      return super.get(idOrConfig as getIDType<S>, { ...config, skipWrite: true }).pipe(
        tap((e) => {
          if (!skipWrite) {
            // fix Akita removing active entity from entity store
            this.getStore().replace(id as ID, e as any);
          }
        }),
      );
    }

    // get single
    if (isID(idOrConfig)) {
      const skipWrite: boolean = config?.skipWrite ?? false;

      return super.get(idOrConfig as getIDType<S>, { ...config, skipWrite: true }).pipe(
        tap((e) => {
          if (!skipWrite) {
            // fix Akita removing active entity from entity store
            this.getStore().replace(id as ID, e as any);
          }
        }),
      );
    }

    // get collection
    return super.get<T>({
      ...config,
      ...(idOrConfig as HttpGetConfig<T>),
    });
  }

  public getPage<T>(pagination?: PaginationParams, config?: HttpGetConfig<T>): Observable<T> {
    config = {
      ...config,
      params: pagination
        ? {
            'pagination.pageSize': pagination.pageSize !== undefined ? String(pagination.pageSize) : '',
            'pagination.page': pagination.page !== undefined ? String(pagination.page) : '',
            search: pagination.page !== undefined ? String(pagination.search) : '',
            ...pagination.filters,
          }
        : {},
      mapResponseFn: (res: unknown & WithPagination<T>) => {
        if (hasPagination(res)) {
          this.getStore().update((state) => ({
            ...state,
            pagination: res.pagination,
          }));
          return this.mapResponse(unwrapFromEnvelope(res as WithPagination<T>, envelopeKey), this.mapper);
        }

        return this.mapResponse(res, this.mapper);
      },
    };

    return this.get<T>({
      ...config,
    }).pipe();
  }

  public overrideResourceName(name: string): void {
    this.overriddenResourceName = name;
  }

  public getStore(): EntityStore<S, getEntityType<S>, getIDType<S>> {
    return this.store;
  }

  public update<T>(
    id: getIDType<S>,
    entity: Partial<getEntityType<S>> | FormData,
    config?: HttpUpdateConfig<T>,
  ): Observable<T> {
    const baseUrl = this.urlBuilder.buildBaseUrl(this.baseUrlSuffix);

    // Guard against empty baseUrl to prevent API calls when organization data is cleared during logout
    // This returns EMPTY observable which completes immediately without emitting any values
    if (!baseUrl || baseUrl.trim() === '') {
      return EMPTY;
    }

    this.setBaseUrl(baseUrl);
    const skipWrite: boolean = config?.skipWrite ?? false;
    entity = this.mapRequest<Partial<getEntityType<S>>>(entity);

    config = {
      url: this.buildUrl(id),
      mapResponseFn: (res: unknown) => {
        return this.mapResponse(res, this.mapper);
      },
      ...config,
      skipWrite: true,
    };

    this.clearPrefixesAndSuffixes();

    return super.update<T>(id, entity, config).pipe(
      tap((e) => {
        if (!skipWrite) {
          // fix Akita removing active entity from entity store
          this.getStore().replace(id as ID, e as any);
        }
      }),
    );
  }

  public updateMany(diff: Partial<getEntityType<S>>[]): Observable<unknown> {
    const dtoDiff = diff.map((d) => this.mapRequest(d));

    this.clearPrefixesAndSuffixes();

    /** {@link NgEntityService} does not support updating of many resources at once with array passed in body hence using lower level {@link HttpClient}. */
    return this.getHttp()
      .patch(this.buildUrl(), dtoDiff)
      .pipe(
        tap((dto: DTO[]) => {
          const entitites: [] = this.mapResponse(dto, this.mapper) as [];
          this.store.upsertMany(entitites);
        }),
      );
  }

  public useResourcePrefix(value: string): ApiEntityService<S> {
    this.resourcePrefix = value;

    return this;
  }

  public useResourceSuffix(value: string): ApiEntityService<S> {
    this.resourceSuffix = value;

    return this;
  }

  public useUrlSuffix(value: string): ApiEntityService<S> {
    this.urlSuffix = value;

    return this;
  }

  private buildUrl(id?: getIDType<S>): string {
    const resourceName = this.overriddenResourceName || this.resourceName;

    // Check if base URL already ends with the resource name and ID (to avoid duplication)
    // This happens when ClientIssuingNamespacedResourceBaseUrlBuilder includes the client-issuing ID
    // and the resource name is 'client-issuings'
    if (id && resourceName === 'client-issuings' && this.baseUrl) {
      const idStr = id.toString();
      const expectedSuffix = `/client-issuings/${idStr}`;
      if (this.baseUrl.endsWith(expectedSuffix)) {
        // Base URL already includes the resource name and ID, don't add them again
        const rawUrl: string[] = [
          this.baseUrl,
          this.resourcePrefix,
          this.resourceSuffix,
          this.urlSuffix,
        ].filter((segment) => !!segment);
        const url = rawUrl.join('/');
        return standardizeUrl(url);
      }
    }

    const rawUrl: string[] = [
      this.baseUrl,
      this.resourcePrefix,
      resourceName,
      this.resourceSuffix,
      id?.toString(),
      this.urlSuffix,
    ].filter((segment) => !!segment);
    const url = rawUrl.join('/');
    return standardizeUrl(url);
  }

  private clearPrefixesAndSuffixes(): void {
    this.useResourcePrefix(null);
    this.useResourceSuffix(null);
    this.useUrlSuffix(null);
    // Note: baseUrlSuffix is intentionally NOT cleared here because it's meant to persist
    // across requests for the same service instance (e.g., AttachmentService with appendToBaseUrl)
  }

  private mapRequest<T>(entity?: T | FormData): T {
    if (!entity) {
      return;
    }

    if (entity instanceof FormData) {
      return entity as unknown as T;
    }

    return this.mapper.toDTO(entity as unknown as Entity) as unknown as T;
  }

  private mapResponse<T>(resource: unknown, mapper: Mapper<Entity, DTO>): T | T[] | Observable<T | T[]> {
    if (isCollection(resource)) {
      return (resource as Array<unknown>).map((res) => mapper.toModel(res as DTO)) as unknown as T[];
    }

    return mapper.toModel(resource as DTO) as unknown as T;
  }
}
